import React, { useState, Fragment } from 'react';
import { StudentLayout } from '../layouts/StudentLayout';
import { DownloadIcon, ChevronDownIcon, ChevronUpIcon, SearchIcon, FilterIcon, CreditCardIcon, CheckCircleIcon, XCircleIcon, ClockIcon } from 'lucide-react';
interface Transaction {
  id: string;
  date: Date;
  description: string;
  amount: number;
  status: 'completed' | 'pending' | 'failed';
  paymentMethod: string;
  type: 'lesson' | 'subscription' | 'refund';
  teacherName?: string;
  lessonDate?: Date;
  receiptUrl?: string;
}
export const TransactionHistoryPage = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([{
    id: 'tx_1',
    date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    description: 'Piano Lesson with Sarah Johnson',
    amount: 45.0,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    type: 'lesson',
    teacherName: 'Sarah Johnson',
    lessonDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    receiptUrl: '#'
  }, {
    id: 'tx_2',
    date: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000),
    description: 'Monthly Subscription - Premium Plan',
    amount: 19.99,
    status: 'completed',
    paymentMethod: 'Visa •••• 4242',
    type: 'subscription',
    receiptUrl: '#'
  }, {
    id: 'tx_3',
    date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
    description: 'Violin Lesson with John Smith',
    amount: 50.0,
    status: 'completed',
    paymentMethod: 'PayPal',
    type: 'lesson',
    teacherName: 'John Smith',
    lessonDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
    receiptUrl: '#'
  }, {
    id: 'tx_4',
    date: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000),
    description: 'Cancelled Lesson - Refund',
    amount: -35.0,
    status: 'completed',
    paymentMethod: 'Original Payment Method',
    type: 'refund',
    receiptUrl: '#'
  }, {
    id: 'tx_5',
    date: new Date(Date.now() - 1 * 60 * 60 * 1000),
    description: 'Guitar Lesson with Maria Garcia',
    amount: 40.0,
    status: 'pending',
    paymentMethod: 'Mastercard •••• 5678',
    type: 'lesson',
    teacherName: 'Maria Garcia',
    lessonDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
  }, {
    id: 'tx_6',
    date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000),
    description: 'Failed Payment - Insufficient Funds',
    amount: 45.0,
    status: 'failed',
    paymentMethod: 'Mastercard •••• 5678',
    type: 'lesson',
    teacherName: 'Sarah Johnson'
  }]);
  const [filter, setFilter] = useState({
    status: 'all',
    type: 'all',
    searchQuery: '',
    dateRange: 'all'
  });
  const [expandedTransaction, setExpandedTransaction] = useState<string | null>(null);
  // Format date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  // Toggle transaction details
  const toggleTransaction = (id: string) => {
    if (expandedTransaction === id) {
      setExpandedTransaction(null);
    } else {
      setExpandedTransaction(id);
    }
  };
  // Handle filter change
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFilter({
      ...filter,
      [name]: value
    });
  };
  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({
      ...filter,
      searchQuery: e.target.value
    });
  };
  // Filter transactions
  const filteredTransactions = transactions.filter(transaction => {
    // Filter by status
    if (filter.status !== 'all' && transaction.status !== filter.status) {
      return false;
    }
    // Filter by type
    if (filter.type !== 'all' && transaction.type !== filter.type) {
      return false;
    }
    // Filter by search query
    if (filter.searchQuery) {
      const query = filter.searchQuery.toLowerCase();
      const matchesDescription = transaction.description.toLowerCase().includes(query);
      const matchesTeacher = transaction.teacherName?.toLowerCase().includes(query);
      if (!matchesDescription && !matchesTeacher) {
        return false;
      }
    }
    // Filter by date range
    if (filter.dateRange !== 'all') {
      const now = new Date();
      const transactionDate = new Date(transaction.date);
      switch (filter.dateRange) {
        case 'today':
          if (transactionDate.toDateString() !== now.toDateString()) {
            return false;
          }
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (transactionDate < weekAgo) {
            return false;
          }
          break;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          if (transactionDate < monthAgo) {
            return false;
          }
          break;
        case 'year':
          const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          if (transactionDate < yearAgo) {
            return false;
          }
          break;
      }
    }
    return true;
  });
  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/30 text-green-400" data-id="element-10157">
            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-10158" />
            Completed
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-900/30 text-amber-400" data-id="element-10159">
            <ClockIcon className="h-3 w-3 mr-1" data-id="element-10160" />
            Pending
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-900/30 text-red-400" data-id="element-10161">
            <XCircleIcon className="h-3 w-3 mr-1" data-id="element-10162" />
            Failed
          </span>;
      default:
        return null;
    }
  };
  return <StudentLayout data-id="element-10163">
      <div className="max-w-6xl mx-auto" data-id="element-10164">
        <div className="mb-6" data-id="element-10165">
          <h1 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-10166">
            Transaction History
          </h1>
          <p className="text-gray-300" data-id="element-10167">View and manage your payment history</p>
        </div>
        {/* Filters */}
        <div className="bg-navy-800 rounded-xl p-4 mb-6" data-id="element-10168">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0" data-id="element-10169">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-10170">
              <div className="flex items-center" data-id="element-10171">
                <FilterIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-10172" />
                <span className="text-sm text-gray-300 mr-2" data-id="element-10173">Filter:</span>
              </div>
              <div className="flex space-x-2" data-id="element-10174">
                <select name="status" value={filter.status} onChange={handleFilterChange} className="bg-navy-700 border border-navy-600 text-white text-sm rounded-lg focus:ring-deep-pink focus:border-deep-pink block p-2" data-id="element-10175">
                  <option value="all" data-id="element-10176">All Statuses</option>
                  <option value="completed" data-id="element-10177">Completed</option>
                  <option value="pending" data-id="element-10178">Pending</option>
                  <option value="failed" data-id="element-10179">Failed</option>
                </select>
                <select name="type" value={filter.type} onChange={handleFilterChange} className="bg-navy-700 border border-navy-600 text-white text-sm rounded-lg focus:ring-deep-pink focus:border-deep-pink block p-2" data-id="element-10180">
                  <option value="all" data-id="element-10181">All Types</option>
                  <option value="lesson" data-id="element-10182">Lessons</option>
                  <option value="subscription" data-id="element-10183">Subscriptions</option>
                  <option value="refund" data-id="element-10184">Refunds</option>
                </select>
                <select name="dateRange" value={filter.dateRange} onChange={handleFilterChange} className="bg-navy-700 border border-navy-600 text-white text-sm rounded-lg focus:ring-deep-pink focus:border-deep-pink block p-2" data-id="element-10185">
                  <option value="all" data-id="element-10186">All Time</option>
                  <option value="today" data-id="element-10187">Today</option>
                  <option value="week" data-id="element-10188">This Week</option>
                  <option value="month" data-id="element-10189">This Month</option>
                  <option value="year" data-id="element-10190">This Year</option>
                </select>
              </div>
            </div>
            <div className="relative" data-id="element-10191">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10192">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-10193" />
              </div>
              <input type="text" placeholder="Search transactions..." value={filter.searchQuery} onChange={handleSearch} className="bg-navy-700 border border-navy-600 text-white text-sm rounded-lg focus:ring-deep-pink focus:border-deep-pink block w-full pl-10 p-2.5" data-id="element-10194" />
            </div>
          </div>
        </div>
        {/* Transactions list */}
        <div className="bg-navy-800 rounded-xl overflow-hidden mb-8" data-id="element-10195">
          <div className="overflow-x-auto" data-id="element-10196">
            {filteredTransactions.length > 0 ? <table className="min-w-full divide-y divide-navy-700" data-id="element-10197">
                <thead className="bg-navy-700" data-id="element-10198">
                  <tr data-id="element-10199">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-10200">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-10201">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-10202">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-10203">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-10204">
                      Payment Method
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider" data-id="element-10205">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-700" data-id="element-10206">
                  {filteredTransactions.map(transaction => <Fragment key={transaction.id} data-id="element-10207">
                      <tr className={`hover:bg-navy-700 transition-colors cursor-pointer ${expandedTransaction === transaction.id ? 'bg-navy-700' : ''}`} onClick={() => toggleTransaction(transaction.id)} data-id="element-10208">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10209">
                          <div className="text-sm text-white" data-id="element-10210">
                            {formatDate(transaction.date)}
                          </div>
                          <div className="text-xs text-gray-400" data-id="element-10211">
                            {formatTime(transaction.date)}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-10212">
                          <div className="text-sm text-white" data-id="element-10213">
                            {transaction.description}
                          </div>
                          {transaction.teacherName && <div className="text-xs text-gray-400" data-id="element-10214">
                              Teacher: {transaction.teacherName}
                            </div>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10215">
                          <div className={`text-sm font-medium ${transaction.amount < 0 ? 'text-green-400' : transaction.status === 'failed' ? 'text-gray-400' : 'text-white'}`} data-id="element-10216">
                            {formatCurrency(transaction.amount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10217">
                          {getStatusBadge(transaction.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-10218">
                          <div className="text-sm text-gray-300" data-id="element-10219">
                            {transaction.paymentMethod}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-10220">
                          <button className="text-deep-pink hover:text-burgundy" onClick={e => {
                      e.stopPropagation();
                      // In a real app, this would download the receipt
                      if (transaction.receiptUrl && transaction.status === 'completed') {
                        window.open(transaction.receiptUrl, '_blank');
                      }
                    }} disabled={!transaction.receiptUrl || transaction.status !== 'completed'} data-id="element-10221">
                            {expandedTransaction === transaction.id ? <ChevronUpIcon className="h-5 w-5" data-id="element-10222" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-10223" />}
                          </button>
                        </td>
                      </tr>
                      {/* Expanded details */}
                      {expandedTransaction === transaction.id && <tr className="bg-navy-700" data-id="element-10224">
                          <td colSpan={6} className="px-6 py-4" data-id="element-10225">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-10226">
                              <div data-id="element-10227">
                                <h4 className="text-sm font-medium text-white mb-2" data-id="element-10228">
                                  Transaction Details
                                </h4>
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm" data-id="element-10229">
                                  <dt className="text-gray-400" data-id="element-10230">
                                    Transaction ID:
                                  </dt>
                                  <dd className="text-white" data-id="element-10231">
                                    {transaction.id}
                                  </dd>
                                  <dt className="text-gray-400" data-id="element-10232">Date:</dt>
                                  <dd className="text-white" data-id="element-10233">
                                    {formatDate(transaction.date)} at{' '}
                                    {formatTime(transaction.date)}
                                  </dd>
                                  <dt className="text-gray-400" data-id="element-10234">Type:</dt>
                                  <dd className="text-white capitalize" data-id="element-10235">
                                    {transaction.type}
                                  </dd>
                                  <dt className="text-gray-400" data-id="element-10236">Status:</dt>
                                  <dd className="text-white" data-id="element-10237">
                                    {getStatusBadge(transaction.status)}
                                  </dd>
                                </dl>
                              </div>
                              <div data-id="element-10238">
                                <h4 className="text-sm font-medium text-white mb-2" data-id="element-10239">
                                  Payment Details
                                </h4>
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm" data-id="element-10240">
                                  <dt className="text-gray-400" data-id="element-10241">Amount:</dt>
                                  <dd className={`font-medium ${transaction.amount < 0 ? 'text-green-400' : transaction.status === 'failed' ? 'text-gray-400' : 'text-white'}`} data-id="element-10242">
                                    {formatCurrency(transaction.amount)}
                                  </dd>
                                  <dt className="text-gray-400" data-id="element-10243">
                                    Payment Method:
                                  </dt>
                                  <dd className="text-white" data-id="element-10244">
                                    {transaction.paymentMethod}
                                  </dd>
                                  {transaction.type === 'lesson' && transaction.lessonDate && <>
                                        <dt className="text-gray-400" data-id="element-10245">
                                          Lesson Date:
                                        </dt>
                                        <dd className="text-white" data-id="element-10246">
                                          {formatDate(transaction.lessonDate)}
                                        </dd>
                                        <dt className="text-gray-400" data-id="element-10247">
                                          Teacher:
                                        </dt>
                                        <dd className="text-white" data-id="element-10248">
                                          {transaction.teacherName}
                                        </dd>
                                      </>}
                                </dl>
                                {transaction.receiptUrl && transaction.status === 'completed' && <div className="mt-4" data-id="element-10249">
                                      <a href={transaction.receiptUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 bg-deep-pink hover:bg-burgundy text-white rounded-lg text-sm" data-id="element-10250">
                                        <DownloadIcon className="h-4 w-4 mr-1" data-id="element-10251" />
                                        Download Receipt
                                      </a>
                                    </div>}
                              </div>
                            </div>
                          </td>
                        </tr>}
                    </Fragment>)}
                </tbody>
              </table> : <div className="text-center py-12" data-id="element-10252">
                <div className="inline-flex justify-center items-center w-16 h-16 rounded-full bg-navy-700 mb-4" data-id="element-10253">
                  <CreditCardIcon className="h-8 w-8 text-gray-400" data-id="element-10254" />
                </div>
                <p className="text-gray-300 mb-2" data-id="element-10255">No transactions found</p>
                <p className="text-sm text-gray-400" data-id="element-10256">
                  Try adjusting your filters or search criteria
                </p>
              </div>}
          </div>
        </div>
      </div>
    </StudentLayout>;
};